// ═══════════════════════════════════════════════════════════════════════════
// commandDispatch/viewCommands.ts — ZOOM + REGEN/REGENALL + PAN view-op
// handlers, extracted verbatim from App.tsx's `submitCommandText` (D5).
// ═══════════════════════════════════════════════════════════════════════════
//
// Both ZOOM and PAN parse their sub-options via regex over `upperStart` /
// `trimmedText` rather than the registry's single-token `resolveAlias` —
// unchanged by D5 (they accept trailing arguments on the same line, which
// `resolveAlias`'s whole-string equality cannot express). REGEN / REGENALL
// are plain bare-verb commands and DO route through `resolvedCommandId`.
// ═══════════════════════════════════════════════════════════════════════════

import { useEditorStore } from "../state/editorStore";

import type { DispatchContext, DispatchResult } from "./types";

/**
 * ZOOM commands (local UI, not store-dispatched).
 * Syntax accepted (case-insensitive, not inside a draft session):
 *   ZE / ZA / ZW / ZP / ZO            — aliases
 *   Z E, Z A, Z W, Z P, Z O            — spaced aliases
 *   ZOOM EXTENTS/ALL/WINDOW/PREVIOUS/OBJECT
 *   ZO <entity-id>                    — zoom to specific entity
 *   ZW x1,y1 x2,y2                    — zoom window by explicit corners (batch mode)
 */
export function handleZoomCommand(ctx: DispatchContext): DispatchResult {
  const {
    draftSession,
    upperStart,
    trimmedText,
    source,
    stageCameraApiRef,
    appendCommandFeed,
    t,
    setCommandText,
  } = ctx;

  if (draftSession) return null;

  // Command-prefix alternation: EN `Z`/`ZOOM` + CIS Russian command name
  // `ПОКАЗАТЬ` and its short `ПОК` (mirrors REGEN's `РЕГЕН`, PAN's `ПАН`).
  // Sub-options stay EN letters (matches AutoCAD's keyword-letter dispatch).
  const zoomMatch = upperStart.match(
    /^(?:Z(?:OOM)?|ПОКАЗАТЬ|ПОК)\s*(E|A|W|P|O|EXTENTS|ALL|WINDOW|PREVIOUS|OBJECT)(?:\s+(.+))?$/,
  );
  if (!zoomMatch) return null;

  const sub = zoomMatch[1] ?? "";
  // Preserve original case for arg (entity ids are case-sensitive)
  const argMatch = trimmedText.match(
    /^(?:Z(?:OOM)?|ПОКАЗАТЬ|ПОК)\s*(?:E|A|W|P|O|EXTENTS|ALL|WINDOW|PREVIOUS|OBJECT)\s+(.+)$/i,
  );
  const arg = argMatch?.[1]?.trim();
  const api = stageCameraApiRef.current;
  if (!api) {
    appendCommandFeed(t("zoom.stage_not_ready"));
    return false;
  }
  const animate =
    source === "user" &&
    (typeof navigator === "undefined" ||
      (navigator as { webdriver?: boolean }).webdriver !== true);

  // CC-1c (D5) FIX #269 — gate the ZOOM view-op telemetry on whether the
  // camera ACTUALLY changed. ZE/ZA and the exact-corner ZW form complete
  // synchronously (always a real fit). ZP only restores when the history
  // ring is non-empty; ZO only fits when the id/selection resolves to
  // bounds; the interactive ZW/ZO forms merely START a pick (the fit
  // happens later, on the corner/entity pick), so none of those is a
  // completed view change and must NOT record a "zoom" success.
  let zoomChanged = false;
  if (sub === "E" || sub === "EXTENTS") {
    api.zoomExtents({ animate });
    appendCommandFeed(t("zoom.extents"));
    zoomChanged = true;
  } else if (sub === "A" || sub === "ALL") {
    api.zoomAll({ animate });
    appendCommandFeed(t("zoom.all"));
    zoomChanged = true;
  } else if (sub === "W" || sub === "WINDOW") {
    if (arg) {
      // Direct two-corner form for batch/scripts: "ZW x1,y1 x2,y2".
      // The literals are DOCUMENT (AutoCAD / WCS) coordinates, exactly as the
      // user reads them off the drawing — they are passed through unchanged.
      // The renderer's offset-world translation is applied at the one seam that
      // owns it, `scene3d/cameraApiAdapter.zoomWindowExact`; converting here as
      // well would double-apply the origin.
      const m = arg.match(
        /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/,
      );
      if (m) {
        api.zoomWindowExact(
          { x: parseFloat(m[1]!), y: parseFloat(m[2]!) },
          { x: parseFloat(m[3]!), y: parseFloat(m[4]!) },
          { animate },
        );
        appendCommandFeed(t("zoom.window_exact", { arg }));
        zoomChanged = true;
      } else {
        appendCommandFeed(t("zoom.window_invalid"));
        setCommandText("");
        return false;
      }
    } else {
      // Interactive window pick — only STARTS picking (or is blocked);
      // the fit fires later on the second-corner pick, so there is no
      // completed view change to record here.
      const ok = api.zoomWindow();
      if (!ok) {
        appendCommandFeed(t("zoom.window_blocked"));
      } else {
        appendCommandFeed(t("zoom.window_pick"));
      }
    }
  } else if (sub === "P" || sub === "PREVIOUS") {
    const restored = api.zoomPrevious({ animate });
    appendCommandFeed(restored ? t("zoom.previous") : t("zoom.previous_none"));
    // Only a successful restore (non-empty history ring) moved the view.
    zoomChanged = restored;
  } else if (sub === "O" || sub === "OBJECT") {
    // #293-4 — the adapter reports a discriminated ZoomObjectResult so
    // each outcome gets its own feed line. The old boolean folded
    // "blocked by another active pick" into the same `false` as the old
    // pick-start path, so ZO during an in-flight ZOOM Window pick told
    // the user to select objects even though no object pick was started
    // and clicks still belonged to the previous pick.
    const result = api.zoomObject(arg, { animate });
    if (result === "fitted") {
      appendCommandFeed(
        arg ? t("zoom.object_with_arg", { arg }) : t("zoom.object_selection"),
      );
    } else if (result === "pick-started") {
      appendCommandFeed(t("zoom.object_pick"));
    } else if (result === "blocked") {
      appendCommandFeed(t("zoom.object_blocked"));
    } else if (arg) {
      appendCommandFeed(t("zoom.object_not_found", { arg }));
    } else {
      // No-id form fitted the current selection but its ids resolved to
      // no usable bounds — nothing moved, say so instead of lying.
      appendCommandFeed(t("zoom.object_selection_no_bounds"));
    }
    // Only a committed fit ("fitted") changed the view: a freshly
    // opened interactive pick records its view-op later via
    // onZoomViewOpComplete, and blocked / no-effect moved nothing.
    zoomChanged = result === "fitted";
  }

  // CC-1c (D5-r9) — ZOOM previously wrote ONLY a transient command-feed
  // line, never a `pushAuditEvent`, so the CC-1a server channel had no
  // ZOOM record to transport. Emit a typed `view-op` audit event here
  // (ТЗ §10.1 / §14) carrying the resolved zoom mode. Geometry is
  // untouched — this is observability only.
  const zoomMode =
    sub === "E" || sub === "EXTENTS"
      ? "extents"
      : sub === "A" || sub === "ALL"
        ? "all"
        : sub === "W" || sub === "WINDOW"
          ? "window"
          : sub === "P" || sub === "PREVIOUS"
            ? "previous"
            : "object";
  // FIX #269 — record the view-op ONLY when the zoom actually changed the
  // view; a no-op ZP (empty history) / ZO (missing id) / an interactive
  // pick that only started must not log a false "zoom" success.
  if (zoomChanged) {
    useEditorStore.getState().recordViewOp({
      opType: "zoom",
      summary: t("audit.viewop_zoom", { mode: zoomMode }),
      source: source === "batch" ? "system" : "commandline",
      detail: { mode: zoomMode },
    });
  }

  setCommandText("");
  return true;
}

/**
 * ── CC-1c — REGEN / REGENALL / PAN view-op telemetry emitters ──────────
 * AutoCAD parity: REGEN/REGENALL recompute the display (no geometry
 * mutation — it happens implicitly via render-state memoisation). REGEN /
 * REGENALL DO complete synchronously, so they emit a `recordViewOp`
 * view-op. PAN is different — the typed command does not yet move the
 * camera (see its branch below), so it records NO telemetry. None of these
 * pass through the command schema / geometryShellReducer, so the completed
 * ones emit ТЗ §10.1 / §14 telemetry through the `recordViewOp` →
 * `pushViewOpAuditEvent` view-op path.
 */
export function handleRegenPanCommands(ctx: DispatchContext): DispatchResult {
  const {
    draftSession,
    upperStart,
    trimmedText,
    source,
    resolvedCommandId,
    appendCommandFeed,
    t,
    setCommandText,
    stageCameraApiRef,
    startPanSession,
  } = ctx;

  if (draftSession) return null;

  const viewOpSource = source === "batch" ? "system" : "commandline";
  if (resolvedCommandId === "REGEN") {
    useEditorStore.getState().recordViewOp({
      opType: "regen",
      summary: t("audit.viewop_regen"),
      source: viewOpSource,
    });
    appendCommandFeed(t("audit.viewop_regen"));
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "REGENALL") {
    useEditorStore.getState().recordViewOp({
      opType: "regenall",
      summary: t("audit.viewop_regenall"),
      source: viewOpSource,
    });
    appendCommandFeed(t("audit.viewop_regenall"));
    setCommandText("");
    return true;
  }
  {
    // PAN view-op router (modelled on the ZOOM router above). Matches the
    // bare verb (PAN/-PAN/P/ПАН) AND the scripted two-point displacement
    // form `-PAN x1,y1 x2,y2`. Preserve the original case for the arg (not
    // needed for numbers, but keeps the parser parallel to ZW-exact).
    const panMatch = upperStart.match(/^(?:-?PAN|P|ПАН)(?:\s+(.+))?$/);
    if (panMatch) {
      const argMatch = trimmedText.match(/^(?:-?PAN|P|ПАН)\s+(.+)$/i);
      const arg = argMatch?.[1]?.trim();
      const api = stageCameraApiRef.current;
      if (!api) {
        // Match the ZOOM guard — never claim a pan when the stage is unwired.
        appendCommandFeed(t("zoom.stage_not_ready"));
        setCommandText("");
        return false;
      }
      if (arg) {
        // ── Scripted displacement: -PAN x1,y1 x2,y2 ──────────────────────
        // dx = x2 - x1, dy = y2 - y1 (world units). Scoped to the paper/main
        // camera (VP-redirected displacement-PAN is a documented deferral —
        // docs/test-gaps/cmd-pan.md). Parser parallels the ZW-exact form.
        const m = arg.match(
          /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/,
        );
        if (!m) {
          appendCommandFeed(t("pan.displacement_invalid"));
          setCommandText("");
          return false;
        }
        const dx = parseFloat(m[3]!) - parseFloat(m[1]!);
        const dy = parseFloat(m[4]!) - parseFloat(m[2]!);
        // api.pan pushes the pre-pan view onto ZOOM-Previous, then commits
        // the displacement; it returns true ONLY when the camera actually
        // moved (non-zero delta). FIX #269 / #R4 — record the `pan` view-op
        // ONLY on a real change, exactly like the ZOOM `zoomChanged` gate
        // and the honest MMB `onPanComplete` emit (a `-PAN p,p` that moves
        // nothing records nothing).
        const moved = api.pan(dx, dy);
        if (moved) {
          appendCommandFeed(t("pan.displacement", { arg }));
          useEditorStore.getState().recordViewOp({
            opType: "pan",
            summary: t("audit.viewop_pan"),
            source: source === "batch" ? "system" : "commandline",
            // cmd-pan P2-5 — record the committed CAMERA-centre delta, NOT
            // the un-negated DRAWING displacement. `api.pan(dx,dy)` moves the
            // camera centre by −(dx,dy) (AutoCAD `-PAN`: the drawing moves
            // +(dx,dy), so the camera moves the opposite way). Interactive
            // MMB/LMB pans record the camera-centre `end − start`, so the
            // scripted branch must negate to AGREE on direction — otherwise
            // a `-PAN 0,0 100,50` audit reads the reverse of the same pan
            // done by hand.
            detail: { deltaX: -dx, deltaY: -dy },
          });
        } else {
          appendCommandFeed(t("pan.displacement_none"));
        }
        setCommandText("");
        return true;
      }
      // ── Bare PAN / P / ПАН → interactive realtime pan session ──────────
      // Enters the hand-cursor LMB-drag mode (Esc/Enter exits). Each LMB
      // drag emits its own honest `pan` view-op via onPanComplete (gated on
      // a non-zero delta), so this entry records NO view-op itself.
      // FINDING 4a — return the session-start result so the arm does NOT
      // claim success when the stage is unwired (parity with the `!api`
      // guard above and the displacement arm). The outer `!api` guard already
      // short-circuits, so this is belt-and-suspenders against future drift.
      const started = startPanSession();
      setCommandText("");
      return started;
    }
  }

  return null;
}
